import { css, html, LitElement, PropertyValues, TemplateResult } from "lit";
import {property, customElement} from 'lit/decorators.js';
import '../../voyage-popup/src/voyage-popup';
import '../../voyage-checkbox/src/voyage-checkbox';
import { VoyageCheckbox } from "../../voyage-checkbox/src/voyage-checkbox";

export interface Content {
    id: string,
    title: string,
    value: string,
    selected: boolean; 
}

@customElement("voyage-passage-plan-text-content")
export class VoyagePassagePlanTextContent extends LitElement {
    @property({type: Boolean}) shown?: boolean;
    @property({type: Boolean}) fileTitle: string = '';
    @property({type: Boolean}) content: Content[] = [];

    constructor() {
        super();

        this._selectAll = this._selectAll.bind(this);
        this._deselectAll = this._deselectAll.bind(this);
        this._renderContentItem = this._renderContentItem.bind(this);
        this._import = this._import.bind(this);
    }

    static get styles() {
        return [ 
            css`
                :host {
                    font-family: MaerskText;
                    font-size: 16px;
                }
                section {
                    max-width: 855px;
                    padding: 24px;
                }
                .headerTextContainer {
                    display: flex;
                    align-items: center;
                    background: var(--neutrals-grey-09);
                    border-bottom: 1px solid var(--neutrals-grey-07);
                }
                .buttons {
                    display: flex;
                    flex-direction: row;
                    justify-content: flex-start;
                    gap: 16px;
                }
                button {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    height: 32px;
                    margin-bottom: 16px;
                    margin-top: 16px;
                    min-width: 80px;
                    font-size: 14px;
                    font-family: MaerskText;
                    padding: 0px 12px 0px 12px;
                    background: none;
                    border: none;
                    outline: none;
                    border-radius: 3px;
                    color: var(--neutrals-grey-02);
                    border: 1px solid var(--neutrals-grey-06, grey);
                }
                
                .text {
                    padding: 16px 16px 16px 16px;
                    box-sizing: border-box;
                    border: 1px solid var(--neutrals-grey-06, grey);
                    border-radius: 2.5px;
                    color: var(--neutrals-grey-04, grey);
                    background-color: unset;
                    font-size: 14px;
                    font-family: MaerskText;
                    margin: 0px;
                }
                .text-container {
                    width: calc(50% - 12px);
                    padding-bottom: 24px;
                }

                .wrapper {
                    display: flex;
                    flex-direction: row;
                    flex-wrap: wrap;
                    gap: 24px;
                }
                .footer {
                    width: calc(100% - 24px);
                    display: flex;
                    justify-content: flex-end;
                    gap:16px;
                    align-items: center;
                    border-top: 1px solid var(--neutrals-grey-07);
                    background-color: var(--neutrals-grey-09, grey);
                    padding-right: 24px;
                }
                voyage-checkbox {
                    padding-bottom: 12px;
                }

                .file-title {
                    padding: 24px 24px 0 24px;
                }

                .file-name{
                    font-weight: bold;
                    color: var(--neutrals-grey-01, grey);
                }

                .note {
                    font-family: MaerskText;
                    font-size: 14px;
                    font-style: normal;
                    font-weight: 400;
                    line-height: 20px;
                    letter-spacing: 0em;
                    text-align: left;
                    color: var(--neutrals-grey-04, grey);
                    margin: 0px;
                }
                #import {
                    background-color: var(--primary-blue-900, blue);
                    color: var(--neutrals-grey-10, white);
                    font-family: MaerskText;
                    font-size: 14px;
                    font-style: normal;
                    font-weight: 400;
                    line-height: 20px;
                    letter-spacing: 0em;
                    text-align: left;
                    border: 1px solid var(--primary-blue-900, blue);
                }
                #selectAll,#deselectAll {
                    margin-top: 0px;
                    margin-bottom: 24px;
                }
            `
        ];
    }

    render() {
        return html`
            <voyage-popup ?shown=${this.shown} @close=${this._close} no-close-button>
                <div class="file-title" id="header" slot="header">Imported file: <span class="file-name">${this.fileTitle}</span></div>
                <section>
                    <div class="buttons">
                        <button id="selectAll" @click=${this._selectAll}>Select All</button>
                        <button id="deselectAll" @click=${this._deselectAll}>Deselect All</button>
                    </div>
                    <div class="wrapper">
                        ${this.content ? this.content.map((field) => {
                            return this._renderContentItem(field);
                        }) : html``}
                    </div>
                    <p class="note">You can edit the selected texts once you have imported to passage plan.</p>
                </section>
                <div class="footer" slot="footer">
                    <button id="cancel" class="footerButton" @click=${this._close}>Cancel</button>
                    <button id="import" class="footerButton action" @click=${this._import}>Import selected</button>
                </div>
            </voyage-popup>
        `
    }

    private _selectAll() {
        if (!this.content) return;
        this.content = this.content.map(el => {
            return {...el, selected: true}
        });
    }

    private _deselectAll() {
        if(!this.content) return;
        this.content = this.content.map(el => {
            return {...el, selected: false}
        });
    }

    private _close() {
        this.dispatchEvent(new CustomEvent('close'));
    }
    
    private _import() {
        const values = this.content?.filter(el => el.value !== "" && el.selected === true);
        this.dispatchEvent(new CustomEvent('import', { detail: { values } }));
        this._close();
    }

    private _updateCheckedElements(selected:boolean, el:Content) {
        if(!this.content) return;

        const itemIndex = this.content.findIndex(x => x.id === el.id);
        this.content[itemIndex] = {...this.content[itemIndex], selected};
        this.requestUpdate();
    }

    private _renderContentItem(el:Content):TemplateResult {
        return html`
            <div class="text-container">
                <voyage-checkbox .checked=${el.selected} .label=${el.title} @input=${(e: Event) => this._updateCheckedElements((<VoyageCheckbox>e.target).checked, el)}></voyage-checkbox>
                <div class="text">${el.value}</div>
            </div>
        `
    }

}
